import { useEffect, useRef } from "react";
import { useAuth } from "@/lib/auth/AuthContext";
import { useUser } from "@/lib/query/hooks/useUser";
import { useLocale, SUPPORTED_LOCALES } from "@/lib/i18n/I18nProvider";

// Reconciles the locally cached locale (localStorage "veltric.locale", which
// pickInitialLocale already used for first paint) with the authoritative
// languageCode stored on the User. Twin of ThemeSync — without it the two
// drift apart on any browser that never ran the settings Save: the language
// dropdown honestly shows the account value while every label renders in the
// browser's language. Mounted only when authenticated so the login screen
// never fires `useUser` and keeps the localStorage/navigator pick.
function LocaleSyncInner() {
  const { data: user } = useUser();
  const { locale, setLocale } = useLocale();
  const remote = user?.languageCode;
  // Same one-push-per-DB-value rule as ThemeSync: correcting every drift would
  // fight a locale the user just picked in this session.
  const applied = useRef<string | null>(null);

  useEffect(() => {
    // No/unknown value on the account → leave the local pick alone rather
    // than dropping the user to the en-GB floor.
    if (!remote || !SUPPORTED_LOCALES.includes(remote)) return;
    if (applied.current === remote) return;
    applied.current = remote;
    if (remote !== locale) setLocale(remote);
  }, [remote, locale, setLocale]);

  return null;
}

export function LocaleSync() {
  const { state } = useAuth();
  if (state.kind !== "authenticated") return null;
  return <LocaleSyncInner />;
}
